import * as qs from 'querystring';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
} from '@material-ui/core';
import { FiSliders } from '@react-icons/all-files/fi/FiSliders';
import { FiX } from '@react-icons/all-files/fi/FiX';
import React, { createContext, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Filters } from '..';
import RefinementList from './RefinementList';
import { authUserSelector } from '~redux/auth/selectors/authUser';
import {
  changedSortType,
  ProductSearchSortType,
  selectProductListFilters,
  selectProductListSortType,
  updatedFilters,
} from '~redux/search/product';
import { useProductSearchSelector } from '~view/widgets/ProductSearchProvider';

export const FilterDialogContext = createContext<{
  filters: Record<string, Record<string, string | undefined>>;
  setFilters: (
    attribute: string,
    filters: Record<string, string | undefined>
  ) => void;
}>({
  filters: {},
  setFilters: () => null,
});

const FilterDialog: React.FC<{ listId: string }> = ({ listId }) => {
  const auth = useSelector(authUserSelector);
  const [isOpen, setOpen] = useState(false);

  const sortType = useProductSearchSelector(selectProductListSortType, listId);
  const [sort, setSort] = useState<ProductSearchSortType>('recommended');
  useEffect(() => {
    setSort(sortType);
  }, [sortType]);

  const refinedItems = useProductSearchSelector(
    selectProductListFilters,
    listId
  );
  const [filters, setFilters] = useState<
    Record<string, Record<string, string | undefined>>
  >({});
  const setAttributeFilter = useCallback((attribute, filter) => {
    setFilters((prev) => {
      const next = { ...prev, [attribute]: filter };
      return next;
    });
  }, []);
  useEffect(() => {
    const defaultFilters = Object.entries(refinedItems).reduce(
      (prev, [attribute, filters]) => {
        prev[attribute] = filters.reduce((prev, filter) => {
          prev[filter] = Filters[attribute]?.items?.[filter];
          return prev;
        }, {} as Record<string, string | undefined>);
        return prev;
      },
      {} as Record<string, Record<string, string | undefined>>
    );
    setFilters(defaultFilters);
  }, [refinedItems]);

  const dispatch = useDispatch();

  const handleSearch = useCallback(() => {
    const query = qs.decode(window.location.search.substring(1));
    const filterQuery = {
      sort,
      ...Object.entries(filters).reduce((prev, [attribute, filter]) => {
        prev[attribute] = Object.keys(filter);
        return prev;
      }, {} as Record<string, string[]>),
    };
    const encodedQuery = qs.encode({ ...query, ...filterQuery });
    window.history.replaceState(
      null,
      '',
      `${window.location.pathname}?${encodedQuery}`
    );
    dispatch(changedSortType({ listId, sort }));
    dispatch(updatedFilters({ listId, items: filters }));
    setOpen(false);
  }, [listId, sort, filters]);

  const handleReset = useCallback(() => {
    setSort('recommended');
    setFilters({});
  }, []);

  return (
    <>
      <Button
        className="rounded-pill d-flex align-items-center"
        onClick={() => setOpen(true)}
        variant="contained"
        disableElevation
      >
        <FiSliders className="mr-2" />
        絞り込み
      </Button>
      <Dialog open={isOpen} onClose={() => setOpen(false)}>
        <DialogTitle
          disableTypography
          className="d-flex justify-content-between align-items-center"
        >
          <h6 className="mb-0 font-weight-bold d-flex align-items-center">
            <FiSliders className="mr-2" />
            絞り込み検索
          </h6>
          <IconButton onClick={() => setOpen(false)}>
            <FiX />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <div className="border-bottom mb-4">
            <h6 className="font-weight-bold">並び順</h6>
            <RadioGroup row>
              <FormControlLabel
                value="recommended"
                control={<Radio color="primary" />}
                label="おすすめ順"
                checked={sort === 'recommended'}
                onClick={() => setSort('recommended')}
              />
              <FormControlLabel
                value="newest"
                control={<Radio color="primary" />}
                label="新着順"
                checked={sort === 'newest'}
                onClick={() => setSort('newest')}
              />
              <FormControlLabel
                value="lowestWholesaleRate"
                control={<Radio color="primary" />}
                label="掛け率の低い順"
                color="primary"
                checked={sort === 'lowestWholesaleRate'}
                onClick={() => setSort('lowestWholesaleRate')}
              />
              <FormControlLabel
                value="lowestPrice"
                control={<Radio color="primary" />}
                label="上代の低い順"
                checked={sort === 'lowestPrice'}
                onClick={() => setSort('lowestPrice')}
              />
            </RadioGroup>
          </div>
          <FilterDialogContext.Provider
            value={{ filters, setFilters: setAttributeFilter }}
          >
            {Object.values(Filters)
              .filter((item) => auth || !item.auth)
              .map((filter) => (
                <RefinementList
                  key={filter.header}
                  header={filter.header}
                  listId={listId}
                  attribute={filter.attribute}
                  listItems={filter.items}
                />
              ))}
          </FilterDialogContext.Provider>
        </DialogContent>
        <DialogActions>
          <Button
            className="w-100"
            variant="contained"
            disableElevation
            onClick={handleReset}
          >
            リセット
          </Button>
          <Button
            className="w-100"
            variant="contained"
            disableElevation
            color="primary"
            onClick={handleSearch}
          >
            検索
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FilterDialog;
